import { createApi } from '@reduxjs/toolkit/query/react';
import {
  TCreateWithdrawalRequest,
  TCreateWithdrawalResponse,
  type TCreateOrderRequest,
  type TCreateOrderResponse,
  type TGetOrderResponse,
  type TGetSelfResponse,
} from '../types/MerchantTypes';
import { baseQuery, getBaseQueryWithReauth } from '../baseQueryAuth';
import { merchantCabinetApi } from '../../features/merchant-cabinet/api';

export interface DisputeModel {
  id: string;
  order: string;
  withdrawal: string | null;
  summa: number;
  comment: string;
  create_dt: string;
  modify_dt: string;
  status: string;
}


export const MerchantApi = createApi({
  reducerPath: 'MerchantApi',
  tagTypes: ['Merchant'],
  baseQuery: getBaseQueryWithReauth(baseQuery),
  endpoints: (build) => ({

    getSelf: build.query<TGetSelfResponse, void>({
      query: () => ({
        url: '/merchant/get_self/',
        method: 'GET',
      }),
      providesTags: [{ type: 'Merchant', id: 'SELF' }],
      transformErrorResponse: (response: any) => response,
    }),

    getMerchantOrder: build.query<TGetOrderResponse, string>({
      query: (orderId: string) => ({
        url: `/merchant/check_order/${orderId}/`,
        method: 'GET',
        headers: {},
      }),
    }),
    createOrder: build.mutation<TCreateOrderResponse, TCreateOrderRequest>({
      query: (body) => ({
        url: '/merchant/create_order/?type=api',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(merchantCabinetApi.util.invalidateTags(['Dashboard', 'Applications']));
        } catch {}
      },
    }),
    cancelOrder: build.mutation<TGetOrderResponse, string>({
      query: (orderId) => ({
        url: `/merchant/cancel_order/${orderId}/`,
        method: 'POST',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(merchantCabinetApi.util.invalidateTags(['Dashboard', 'Applications']));
        } catch {}
      },
    }),
    createWithdrawal: build.mutation<TCreateWithdrawalResponse, TCreateWithdrawalRequest>({
      query: (body) => ({
        url: '/merchant/create_withdrawal/?type=api',
        method: 'POST',
        body,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(merchantCabinetApi.util.invalidateTags(['Dashboard', 'Applications']));
        } catch {}
      },
    }),
    downloadReport: build.query<Blob, {
      type: 'deposit' | 'withdrawal';
      status?: string;
      date_from?: string;
      date_to?: string;
    }>({
      query: ({ type, status, date_from, date_to }) => {
        // Формируем query-параметры
        const params = new URLSearchParams();
        params.append('type', type);
        if (status)      params.append('status', status);
        if (date_from)   params.append('date_from', date_from);
        if (date_to)     params.append('date_to', date_to);

        return {
          url: `merchant/download_report/?${params.toString()}`,
          method: 'GET',
          // Мы хотим получить «сырые» данные (Blob/xlsx), поэтому ниже:
          responseHandler: async (response: Response) => {
            // Если статус не 200, пробрасываем ошибку
            if (!response.ok) throw new Error('Failed to download report');
            return response.blob();
          },
        } as const;
      },
    }),
    getCheckOrderOnDispute: build.query<
      { type: 'order'; order_status: string; real_income: number },
      string // orderId
    >({
      query: (orderId) => ({
        url: `disputes/check_order_on_dispute/${orderId}/`,
        method: 'GET',
      }),
    }),
    createDispute: build.mutation<any, FormData>({
      query: (formData) => ({
        url: `disputes/`,
        method: 'POST',
        body: formData,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(merchantCabinetApi.util.invalidateTags(['Dashboard', 'Applications', 'Disputes']));
        } catch {}
      },
    }),
    getAllDisputes: build.query<DisputeModel[], void>({
      query: () => 'disputes/self/',
    }),
  }),
});

export const {
  useGetSelfQuery,
  useGetMerchantOrderQuery,
  useCreateOrderMutation,
  useCancelOrderMutation,
  useCreateWithdrawalMutation,
  useLazyDownloadReportQuery,
  useLazyGetCheckOrderOnDisputeQuery,
  useCreateDisputeMutation,
  useGetAllDisputesQuery,
} = MerchantApi;
